import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import ReorderableLookupCards from '../../components/ui/ReorderableLookupCards'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import { exportClassificationLookups } from './api/classificationLookupApi'
import { classificationLookupColumns } from './components/ClassificationLookupColumns'
import ClassificationLookupFormDialog from './components/ClassificationLookupFormDialog'
import { CLASSIFICATION_LOOKUP_CONFIG } from './config'
import { useClassificationLookups } from './hooks/useClassificationLookups'
import type {
  ClassificationLookup,
  ClassificationLookupKind,
  ClassificationLookupUpsert,
} from './types'
// Layout sınıfları (admin-page/admin-form/admin-row-actions/admin-switch) paylaşılan — 012'den yeniden kullanılır.
import '../admin/admin.css'

interface Props {
  kind: ClassificationLookupKind
}

/**
 * <b>Admin → Parametreler → Stratejik Hedefler / Katkılar</b> yönetim ekranı (127). İki katalog tek
 * bileşendir; API tabanı, önbellek anahtarı, metinler ve dışa aktarma adı `kind`'dan gelir (`config.ts`).
 * Davranış Etkinlik Türü ekranıyla (`EventTypesPage`) aynı: sıralanabilir liste (sayfalama/arama yok) +
 * ekle/düzenle + durum anahtarı + onaylı soft-delete; dar ekranda kart listesi. Başlık = menü adı.
 *
 * <p>İç ekran `key={kind}` ile çizilir: iki rota aynı bileşeni kullandığından, rotalar arasında
 * geçişte açık form, düzenlenen kayıt ya da alan hatası diğer kataloğa taşınmasın diye ekran yeniden
 * kurulur (çağıran ayrıca key vermese de).
 */
export default function TaskClassificationLookupPage({ kind }: Readonly<Props>) {
  return <ClassificationLookupScreen key={kind} kind={kind} />
}

function ClassificationLookupScreen({ kind }: Readonly<Props>) {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const config = CLASSIFICATION_LOOKUP_CONFIG[kind]
  const k = (suffix: string, options?: Record<string, unknown>) =>
    t(`${config.i18nPrefix}.${suffix}`, options)
  const { list, create, update, changeStatus, remove, reorder } = useClassificationLookups(kind)
  const toast = useToast()
  // 126 — Kısıtlı Yetkili asistan ekranı SALT OKUNUR görür: Yeni, sıralama, anahtar, düzenle ve sil
  // hiç çizilmez; durum rozetle okunmaya devam eder.
  const { readOnly } = useProxyMode()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ClassificationLookup | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const rows = list.data?.content ?? []
  const submitting = create.isPending || update.isPending
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const busyId = pendingRowId(changeStatus, remove)

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (row: ClassificationLookup) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock/generic toast'ı merkez (019) basar.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(k('errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: ClassificationLookupUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', k('toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          notify('success', k('toast.created'))
          setFormOpen(false)
        },
        onError: handleMutationError,
      })
    }
  }

  const onToggle = (row: ClassificationLookup, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () => notify('success', k('toast.statusChanged')),
        // Hata (optimistic-lock/generic) → merkezî toast (019).
      },
    )
  }

  // Satırı yukarı/aşağı taşı → görünen sırayı komşuyla takas edip TÜM id sırasını backend'e yaz (1..N).
  const onMove = (row: ClassificationLookup, dir: 'up' | 'down') => {
    const idx = rows.findIndex((r) => r.id === row.id)
    const swapWith = dir === 'up' ? idx - 1 : idx + 1
    if (idx < 0 || swapWith < 0 || swapWith >= rows.length) return
    const orderedIds = rows.map((r) => r.id)
    ;[orderedIds[idx], orderedIds[swapWith]] = [orderedIds[swapWith], orderedIds[idx]]
    reorder.mutate(orderedIds, {
      onError: () => notify('error', t('common.reorder.error')),
    })
  }

  const onDelete = (row: ClassificationLookup) => {
    confirmDialog({
      header: k('delete.title'),
      message: k('delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: k('delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', k('toast.deleted')),
          // Hata → merkezî toast (019).
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <ClassificationLookupFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        i18nPrefix={config.i18nPrefix}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t(config.menuLabelKey)}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton
              onExport={() => exportClassificationLookups(kind)}
              disabled={rows.length === 0}
            />
            {!readOnly && (
              <Button icon="pi pi-plus" label={k('actions.new')} onClick={openCreate} />
            )}
          </>
        }
      />

      {/* Sıralanabilir (SORT_INDEX) liste → sayfalama kapalı, kolon sıralaması yok; daima sıra düzeninde. */}
      {isMobile ? (
        <ReorderableLookupCards
          items={rows}
          loading={list.isLoading}
          busyId={busyId}
          reordering={reorder.isPending}
          onMove={onMove}
          onEdit={openEdit}
          onToggle={onToggle}
          onDelete={onDelete}
          readOnly={readOnly}
          emptyTitle={k('empty.title')}
          emptyDescription={k('empty.desc')}
          labels={{
            active: k('status.active'),
            passive: k('status.passive'),
            edit: k('actions.edit'),
            delete: k('actions.delete'),
            toggle: k('actions.toggle'),
            moveUp: t('common.reorder.up'),
            moveDown: t('common.reorder.down'),
            menu: t('common.actionsMenu'),
          }}
        />
      ) : (
        <DataTable
          value={rows}
          loading={list.isLoading}
          paginator={false}
          emptyMessage={<EmptyState title={k('empty.title')} description={k('empty.desc')} />}
        >
          {classificationLookupColumns({
            t,
            i18nPrefix: config.i18nPrefix,
            localized,
            busyId,
            rowCount: rows.length,
            reordering: reorder.isPending,
            onMove,
            onEdit: openEdit,
            onToggle,
            onDelete,
            readOnly,
          })}
        </DataTable>
      )}
    </div>
  )
}

/**
 * İşlemi süren satırın kimliği (durum değişimi ya da silme) — tablo/kart o satırın kontrollerini kilitler.
 * İç içe ternary yerine ayrı fonksiyon (Sonar typescript:S3358).
 */
function pendingRowId(
  changeStatus: { isPending: boolean; variables?: { id: number } },
  remove: { isPending: boolean; variables?: number },
): number | null {
  if (changeStatus.isPending) return changeStatus.variables?.id ?? null
  if (remove.isPending) return remove.variables ?? null
  return null
}
